import { useFlag } from '@reflag/react-sdk';
import * as React from 'react';

import { SiteFeatureKey, SiteVisibility } from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { SITE_FEATURES_MAP } from '@gitbook/app-pricing';
import { PlainCodeBlock } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    Code,
    Description,
    Heading,
    LoadingPanel,
    Panel,
    Tab,
    Tabs,
    TextInput,
} from '@gitbook/spine-react';

import { DocumentationLink, PaidFeatureIndicator } from '../../../../../Components';
import {
    apiResource,
    useSiteContext,
    useSuspensePaginatedAPIQuery,
    useUpgradeSite,
} from '../../../../../state';
import { createDocumentationURL } from '../../../../../utils';

/**
 * Loading shim for the embed assistant panel
 */
export function SiteEmbedPanel() {
    const { isEnabled } = useFlag('EMBED_ASSISTANT_PANEL');
    return isEnabled ? (
        <React.Suspense fallback={<LoadingPanel />}>
            <SiteEmbedPanelSettings />
        </React.Suspense>
    ) : null;
}

const FEATURE_KEY = SiteFeatureKey.SitesEmbed;
const FEATURE = SITE_FEATURES_MAP[FEATURE_KEY];

/**
 * Panel to configure and preview the assistant embed
 */
function SiteEmbedPanelSettings() {
    const { site, organizationId } = useSiteContext();

    const [selectedTab, setSelectedTab] = React.useState<
        'script' | 'npm-package' | 'react-components'
    >('script');
    const [, setLoading] = React.useState(true);

    const { data: shareLinks } = useSuspensePaginatedAPIQuery(
        apiResource('listSiteShareLinks', { organizationId, siteId: site.id }),
        {
            skip: site.visibility !== SiteVisibility.ShareLink,
        }
    );

    const upgrade = useUpgradeSite(site, FEATURE_KEY);

    const publishedUrl = !site.published
        ? undefined
        : site.visibility === SiteVisibility.ShareLink
          ? shareLinks?.items.find((link) => !!link.active)?.urls.published
          : site.urls.published;
    const siteUrl = publishedUrl?.endsWith('/') ? publishedUrl.slice(0, -1) : publishedUrl;
    const previewSrc = `${siteUrl}/~gitbook/embed/demo`;

    const logged = React.useRef(false);
    const logHover = React.useCallback(() => {
        if (logged.current) {
            return;
        }
        logged.current = true;
        analytics.logEvent(
            analytics.createSiteEvent(
                { id: site.id, organization: organizationId },
                { type: 'site_embed_panel_hover' }
            )
        );
    }, [organizationId, site.id]);

    if (!siteUrl) {
        return null;
    }

    /**
     * Alert used to hide site-specific instructions in case the site has not upgraded
     */
    const upgradePanel: React.ReactElement | undefined = upgrade ? (
        <Alert
            size="small"
            kind="upgrade"
            details={`${upgrade.label} to ${FEATURE.upgradeTitle?.toLowerCase()}.`}
            action={{
                label: upgrade.label,
                onPress: upgrade.onPress,
            }}
        />
    ) : undefined;

    return (
        <Panel>
            <div className="flex items-center justify-start gap-2">
                <Heading level={2}>Embed in your product</Heading>
                <PaidFeatureIndicator upgrade={upgrade} />
                <Button
                    kind="secondary"
                    label="Read the docs"
                    leadingIcon={Icon.HelpCircle}
                    target="_blank"
                    className="ml-auto"
                    href={createDocumentationURL(
                        'publishing-documentation/gitbook-assistant/adding-the-assistant-to-your-website-or-app'
                    )}
                />
            </div>
            <Description className="mb-4">
                Make the assistant available in your own product or site to surface knowledge
                exactly when your users need it.
            </Description>
            <div className="flex flex-col gap-8 xl:flex-row xl:items-start">
                <div className="flex flex-col gap-4 xl:flex-1">
                    <Tabs className="border-b">
                        <Tab
                            active={selectedTab === 'script'}
                            label="Script"
                            onPress={() => setSelectedTab('script')}
                        />
                        <Tab
                            active={selectedTab === 'npm-package'}
                            label="NPM package"
                            onPress={() => setSelectedTab('npm-package')}
                        />
                        <Tab
                            active={selectedTab === 'react-components'}
                            label="React"
                            onPress={() => setSelectedTab('react-components')}
                        />
                    </Tabs>

                    {selectedTab === 'script' && (
                        <EmbedScriptDocs siteUrl={siteUrl} upgrade={upgradePanel} />
                    )}
                    {selectedTab === 'npm-package' && (
                        <NpmPackageDocs siteUrl={siteUrl} upgrade={upgradePanel} />
                    )}
                    {selectedTab === 'react-components' && (
                        <ReactComponentsDocs siteUrl={siteUrl} upgrade={upgradePanel} />
                    )}
                </div>
                <div className="flex overflow-hidden rounded-lg border border-base bg-base max-xl:order-first xl:flex-1">
                    <iframe
                        key={previewSrc}
                        title="Assistant Preview"
                        sandbox="allow-same-origin allow-scripts"
                        src={previewSrc}
                        width="100%"
                        height="700px"
                        style={{ border: 0, margin: 0 }}
                        onLoad={() => setLoading(false)}
                        onPointerEnter={logHover}
                    />
                </div>
            </div>
        </Panel>
    );
}

/**
 * Documentation for the script embed
 */
function EmbedScriptDocs(props: { siteUrl: string; upgrade?: React.ReactElement }) {
    const { siteUrl, upgrade } = props;

    const includeScript = `<script src="${siteUrl}/~gitbook/embed/script.js"></script>`;
    const configureAssistant = `window.GitBook('configure', {
    suggestions: [
        'Help me get started',
        'What can I ask you?',
        'Show me tips and tricks',
    ],
})`;

    return (
        <>
            <Heading level={4} className="text-ui-heading-small">
                Include the script
            </Heading>
            <p>
                Add the following script tag to instantly embed the assistant as a widget in your
                product or site.
            </p>
            {upgrade ? (
                upgrade
            ) : (
                <>
                    <PlainCodeBlock
                        syntax="html"
                        code={includeScript}
                        showCopyButton={true}
                        overflow="wrap"
                    />
                    <p>
                        Or include the script directly from its source by using the following URL:
                    </p>
                    <TextInput value={`${siteUrl}/~gitbook/embed/script.js`} readOnly copyable />
                </>
            )}
            <p>
                Once the script has loaded, the assistant widget will be available on the bottom
                right of the page.
            </p>
            <Heading level={4} className="mt-4 text-ui-heading-small">
                Configure the assistant
            </Heading>
            <p>
                You can control and configure the widget using the <Code>window.GitBook()</Code>{' '}
                methods that are available after the script has loaded. For example, to set
                suggested questions:
            </p>
            <PlainCodeBlock
                syntax="javascript"
                code={configureAssistant}
                showCopyButton={true}
                overflow="wrap"
            />
            <p>
                For the full list of methods, check out the{' '}
                <DocumentationLink path="publishing-documentation/gitbook-assistant/reference">
                    the reference
                </DocumentationLink>
                .
            </p>
        </>
    );
}

/**
 * Documentation for the NPM package embed
 */
function NpmPackageDocs(props: { siteUrl: string; upgrade?: React.ReactElement }) {
    const { siteUrl, upgrade } = props;

    const includePackage = `import { createGitBook } from '@gitbook/embed';

const gitbook = createGitBook({
    siteURL: '${siteUrl}',
});

const iframe = document.createElement('iframe');
iframe.src = gitbook.getFrameURL();

const frame = gitbook.createFrame(iframe);`;

    const configureAssistant = `frame.configure({
    suggestions: [
        'Help me get started',
        'What can I ask you?',
        'Show me tips and tricks',
    ],
})`;

    return (
        <>
            <Heading level={4} className="text-ui-heading-small">
                Include the package
            </Heading>
            <p>Install the NPM package to include it in your web application.</p>
            <PlainCodeBlock
                syntax="shell"
                code={`npm install @gitbook/embed`}
                overflow="wrap"
                showCopyButton={true}
            />
            <p>
                Then, import the package and call the <Code>createGitBook</Code> function with your
                site URL.
            </p>
            {upgrade ? (
                upgrade
            ) : (
                <PlainCodeBlock
                    syntax="javascript"
                    overflow="wrap"
                    code={includePackage}
                    showCopyButton={true}
                />
            )}
            <p>
                You can use the resulting instance to get the frame URL and create a frame to embed
                the assistant anywhere you want.
            </p>

            <Heading level={4} className="mt-4 text-ui-heading-small">
                Configure the assistant
            </Heading>
            <p>
                The <Code>gitbook.createFrame()</Code> function returns a set of methods to control
                and configure the assistant. For example, to set suggested questions:
            </p>
            <PlainCodeBlock
                syntax="javascript"
                code={configureAssistant}
                showCopyButton={true}
                overflow="wrap"
            />
            <p>
                For the full list of methods, check out the{' '}
                <DocumentationLink path="publishing-documentation/gitbook-ai-assistant/configuration/reference">
                    the reference
                </DocumentationLink>
                .
            </p>
        </>
    );
}

/**
 * Documentation for the React components embed
 */
function ReactComponentsDocs(props: { siteUrl: string; upgrade?: React.ReactElement }) {
    const { siteUrl, upgrade } = props;

    const includePackage = `import { GitBookProvider, GitBookAssistantFrame } from '@gitbook/embed/react';

<GitBookProvider siteURL="${siteUrl}">
    <GitBookAssistantFrame />
</GitBookProvider>`;

    const configureAssistant = `<GitBookAssistantFrame suggestions={[
    'Help me get started',
    'What can I ask you?',
    'Show me tips and tricks',
]} />`;

    return (
        <>
            <Heading level={4} className="text-ui-heading-small">
                Include the components
            </Heading>
            <p>Install the NPM package to include it in your web application.</p>
            <PlainCodeBlock
                syntax="shell"
                code={`npm install @gitbook/embed`}
                overflow="wrap"
                showCopyButton={true}
            />
            <p>
                Then, import the prebuilt React components and wrap the <Code>GitBookProvider</Code>{' '}
                around your application, using your site URL.
            </p>
            {upgrade ? (
                upgrade
            ) : (
                <PlainCodeBlock
                    syntax="javascript"
                    overflow="wrap"
                    code={includePackage}
                    showCopyButton={true}
                />
            )}
            <p>
                You can then use the <Code>GitBookAssistantFrame</Code> component to embed the
                assistant anywhere you want.
            </p>
            <PlainCodeBlock
                syntax="javascript"
                overflow="wrap"
                code={includePackage}
                showCopyButton={true}
            />

            <Heading level={4} className="mt-4 text-ui-heading-small">
                Configure the assistant
            </Heading>
            <p>
                You can pass options to the <Code>GitBookProvider</Code> component to configure the
                assistant. For example, to set suggested questions:
            </p>
            <PlainCodeBlock
                syntax="javascript"
                code={configureAssistant}
                showCopyButton={true}
                overflow="wrap"
            />
            <p>
                For the full list of methods, check out the{' '}
                <DocumentationLink path="publishing-documentation/gitbook-assistant/reference">
                    the reference
                </DocumentationLink>
                .
            </p>
        </>
    );
}
